'use client';

import { useEffect, useRef } from 'react';

// Shader source ported from docs/stitch/shader.html (Stitch "Kinetic" screen).
const VERTEX_SHADER_SOURCE = `attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAGMENT_SHADER_SOURCE = `precision highp float;
uniform float u_time;
uniform vec2 u_resolution;

void main() {
    vec2 uv = (gl_FragCoord.xy * 2.0 - u_resolution.xy) / min(u_resolution.x, u_resolution.y);

    // Technical grid
    float gridWidth = 0.002;
    float gridFreq = 12.0;
    float grid = (step(1.0 - gridWidth, fract(uv.x * gridFreq + u_time * 0.03)) +
                  step(1.0 - gridWidth, fract(uv.y * gridFreq - u_time * 0.01)));

    // Energy flow
    float flow = 0.0;
    for(float i = 1.0; i < 3.0; i++) {
        uv.x += 0.3 / i * sin(i * uv.y + u_time * 0.4 + i);
        uv.y += 0.3 / i * cos(i * uv.x + u_time * 0.4 + i);
        flow += abs(0.005 / uv.y);
    }

    // Colors (Wilkins Green #169E51)
    vec3 baseColor = vec3(0.086, 0.62, 0.318);
    vec3 bgColor = vec3(1.0, 1.0, 1.0);

    vec3 finalColor = mix(bgColor, baseColor, flow * 0.1);
    finalColor += grid * 0.02;

    gl_FragColor = vec4(finalColor, 1.0);
}`;

function compileShader(
  gl: WebGLRenderingContext,
  shaderType: number,
  shaderSource: string,
): WebGLShader | null {
  const shader = gl.createShader(shaderType);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, shaderSource);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

/**
 * Renders the Stitch "Kinetic" WebGL background behind the page.
 * Skips entirely when prefers-reduced-motion is set or WebGL is unavailable.
 */
export function ShaderBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );
    if (reducedMotionQuery.matches) {
      return;
    }

    const gl = canvas.getContext('webgl');
    if (!gl) {
      return;
    }

    const vertexShader = compileShader(
      gl,
      gl.VERTEX_SHADER,
      VERTEX_SHADER_SOURCE,
    );
    const fragmentShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      FRAGMENT_SHADER_SOURCE,
    );
    if (!vertexShader || !fragmentShader) {
      return;
    }

    const program = gl.createProgram();
    if (!program) {
      return;
    }
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      return;
    }
    gl.useProgram(program);

    const positionAttributeLocation = gl.getAttribLocation(
      program,
      'a_position',
    );
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(positionAttributeLocation);
    gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    const timeUniformLocation = gl.getUniformLocation(program, 'u_time');
    const resolutionUniformLocation = gl.getUniformLocation(
      program,
      'u_resolution',
    );

    const syncCanvasSizeToLayout = () => {
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }
    };
    const resizeObserver = new ResizeObserver(syncCanvasSizeToLayout);
    resizeObserver.observe(canvas);
    syncCanvasSizeToLayout();

    let animationFrameId: number | null = null;

    const renderFrame = (elapsedMilliseconds: number) => {
      syncCanvasSizeToLayout();
      gl.uniform1f(timeUniformLocation, elapsedMilliseconds * 0.001);
      gl.uniform2f(resolutionUniformLocation, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      animationFrameId = requestAnimationFrame(renderFrame);
    };

    const startRenderLoop = () => {
      if (animationFrameId === null) {
        animationFrameId = requestAnimationFrame(renderFrame);
      }
    };
    const stopRenderLoop = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopRenderLoop();
      } else {
        startRenderLoop();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Fade the animated background as the reader scrolls past the hero so it
    // stops competing with body text. Throttled through requestAnimationFrame.
    let scrollFadeFrameId: number | null = null;
    const applyScrollFade = () => {
      scrollFadeFrameId = null;
      const fadeDistance = Math.max(window.innerHeight * 0.7, 1);
      const scrolledFraction = Math.min(window.scrollY / fadeDistance, 1);
      canvas.style.opacity = (1 - scrolledFraction * 0.82).toFixed(3);
    };
    const requestScrollFade = () => {
      if (scrollFadeFrameId === null) {
        scrollFadeFrameId = requestAnimationFrame(applyScrollFade);
      }
    };
    window.addEventListener('scroll', requestScrollFade, { passive: true });
    applyScrollFade();

    startRenderLoop();

    return () => {
      stopRenderLoop();
      if (scrollFadeFrameId !== null) {
        cancelAnimationFrame(scrollFadeFrameId);
      }
      window.removeEventListener('scroll', requestScrollFade);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      resizeObserver.disconnect();
      gl.deleteBuffer(positionBuffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      const loseContextExtension = gl.getExtension('WEBGL_lose_context');
      if (loseContextExtension) {
        loseContextExtension.loseContext();
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 h-full w-full transition-opacity duration-200 ease-out [will-change:opacity]"
    />
  );
}
